import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { RichTextEditor, textToEditorHtml } from "@/components/RichTextEditor";
import { EmailTemplatesMenu, firstNameOf, insertTemplateHtml } from "@/components/EmailTemplatesMenu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Bot, FolderInput, Loader2, Reply as ReplyIcon, ReplyAll } from "lucide-react";
import { AttachmentsBox } from "@/components/mail/AttachmentsBox";
import { buildAttachmentsPayload, hasInlineImage, mergeItems, splitInlineImages, uploadItem } from "@/components/mail/attachments";
import { caseFileSource, caseScope } from "@/components/mail/fileSources";
import { toast } from "sonner";
import { CasePicker } from "@/pages/inbox/CasePicker";
import { SignoffHint } from "@/pages/inbox/InboxActions";


export function DeptReplyDialog({ email, api, me, suggestedCase, caseLink, fromLabel, initialBody = "", initialReplyAll = false, draftSource = "", onClose, onSent }) {
  const [body, setBody] = useState(initialBody || "");
  const [bodyHtml, setBodyHtml] = useState(() => textToEditorHtml(initialBody || ""));
  const [alsoDone, setAlsoDone] = useState(true);
  const [logOn, setLogOn] = useState(true);
  const [replyAll, setReplyAll] = useState(!!initialReplyAll);
  const [picked, setPicked] = useState(suggestedCase || null);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const [sending, setSending] = useState(false);
  const fileSource = useMemo(() => caseFileSource(api), [api]);
  const messageAttachments = useMemo(() => ({ messageId: email.id, list: () => api.attachments(email.id) }), [api, email.id]);

  const addFiles = (list) => {
    const next = mergeItems(files, Array.from(list || []).map(uploadItem));
    if (!next) { toast.error("Attachments must total under 25 MB"); return; }
    setFiles(next);
  };

  const submit = async () => {
    if (logOn && !picked) { toast.error("Pick the customer file to log the reply on (or untick logging)"); return; }
    setSending(true);
    try {
      const { html: sendHtml, inline } = splitInlineImages(bodyHtml);
      const attachments = await buildAttachmentsPayload(files, api.uploadChunk, setProgress, inline);
      const r = await api.reply(email.id, {
        body, body_html: sendHtml, reply_all: replyAll, case_id: logOn ? picked.case_id : "", move_to_done: alsoDone,
        actor: me?.name || me?.email || "", attachments, draft_source: draftSource || "",
      });
      (r.warnings || []).forEach(w => toast.warning(w));
      toast.success(`Reply sent${(r.to || []).length ? ` to ${r.to.join(", ")}` : ""}${r.attachments ? ` with ${r.attachments} attachment${r.attachments === 1 ? "" : "s"}` : ""}${r.logged_to ? ` · logged on ${r.logged_to}` : ""}${r.moved_to_done ? " · moved to Done" : ""}`);
      onSent(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSending(false); setProgress(null); }
  };

  const tryClose = () => { if (sending) return; if ((body.trim() && body.trim() !== (initialBody || "").trim()) || hasInlineImage(bodyHtml) || files.length) { if (!window.confirm("Discard this reply? What you've typed will be lost.")) return; } onClose(); };

  return (
    <Dialog open onOpenChange={(v) => !v && tryClose()}>
      <DialogContent className="max-w-4xl max-h-[94vh] overflow-y-auto" aria-describedby={undefined} data-testid="reply-dialog"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            {replyAll ? <ReplyAll className="w-4 h-4 text-blue-600" /> : <ReplyIcon className="w-4 h-4 text-blue-600" />}
            {replyAll ? "Reply all" : "Reply"}
            <span className="text-[11px] font-normal text-muted-foreground">from {fromLabel || `${api.dept}@`}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground border rounded px-2 py-1.5 bg-slate-50 flex items-start gap-3">
            <div className="min-w-0 flex-1">
              {email.from_us && (email.to || []).length > 0 ? (
                <div className="truncate" data-testid="reply-to-line"><b>To:</b> {(email.to || []).filter((r) => (r.address || "").toLowerCase() !== (fromLabel || "").toLowerCase()).map((r) => `${r.name ? `${r.name} ` : ""}<${r.address}>`).join(", ")}{replyAll && (email.cc || []).length > 0 && <span className="text-slate-400"> + cc {(email.cc || []).map((r) => r.address).join(", ")}</span>}<span className="text-slate-400"> · this was our email — the reply goes to the people we wrote to, not back to {fromLabel || `${api.dept}@`}</span></div>
              ) : (
                <div className="truncate" data-testid="reply-to-line"><b>To:</b> {email.from_name || ""} &lt;{email.from_email || ""}&gt;{replyAll && <span className="text-slate-400"> + everyone on the thread</span>}</div>
              )}
              <div className="truncate"><b>Re:</b> {email.subject || "(no subject)"}</div>
            </div>
            <label className="flex items-center gap-1.5 text-[11px] cursor-pointer shrink-0 pt-0.5">
              <input type="checkbox" checked={replyAll} onChange={(e) => setReplyAll(e.target.checked)} data-testid="reply-all-toggle" />
              <ReplyAll className="w-3 h-3" />Reply all
            </label>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium text-slate-600">Your reply</span>
              <EmailTemplatesMenu dept={api.dept} region={api.region} testId="reply-templates"
                context={{ first_name: firstNameOf(email.from_name), customer_name: picked?.customer_name || "", sf_number: picked?.sf || "", my_name: me?.name || "" }}
                getCurrent={() => ({ body_html: bodyHtml, body_text: body, subject: "" })}
                onInsert={({ body_html }) => setBodyHtml((cur) => insertTemplateHtml(cur, body_html))} />
            </div>
            <RichTextEditor value={bodyHtml} onChange={({ html, text }) => { setBodyHtml(html); setBody(text); }} minHeight={280} autoFocus testId="reply-body" images
              placeholder={`Type your reply — it's sent from ${fromLabel || `${api.dept}@`} on the same thread, with the original quoted underneath`} />
            {initialBody && <div className="mt-1 text-[10px] text-violet-700 flex items-center gap-1" data-testid="reply-ai-draft-hint"><Bot className="w-3 h-3" />Pre-filled draft — read it through and edit before sending.</div>}
            <div className="mt-1"><SignoffHint /></div>
          </div>
          <AttachmentsBox items={files} onChange={setFiles} disabled={sending} progress={progress} fileSource={fileSource} defaultScope={caseScope(picked)} messageAttachments={messageAttachments} testPrefix="reply"
            onScopePicked={(s) => { if (!picked && s?.case_id) setPicked({ case_id: s.case_id, sf: s.sf, customer_name: s.customer_name, how: "search" }); }} />
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={logOn} onChange={(e) => setLogOn(e.target.checked)} data-testid="reply-log-toggle" />
              <FolderInput className="w-3.5 h-3.5 text-indigo-600" />Log this reply on the customer file timeline
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={alsoDone} onChange={(e) => setAlsoDone(e.target.checked)} data-testid="reply-move-done" />
              Move the email to Done after sending
            </label>
          </div>
          {logOn && (
            <div className="border rounded p-2 bg-indigo-50/40" data-testid="reply-case-section">
              <CasePicker api={api} value={picked} onChange={setPicked} suggested={suggestedCase} caseLink={caseLink} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={tryClose} disabled={sending}>Cancel</Button>
          <Button onClick={submit} disabled={sending || (!body.trim() && !hasInlineImage(bodyHtml))} data-testid="reply-submit">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ReplyIcon className="w-4 h-4 mr-1" />}
            {sending ? (progress ? "Uploading…" : "Sending…") : logOn && picked ? `Send & log on ${picked.sf || picked.customer_name}` : "Send reply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LogToCaseDialog({ email, api, me, suggestedCase, caseLink, onClose, onLogged }) {
  const [picked, setPicked] = useState(suggestedCase || null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.logToCase(email.id, { case_id: picked.case_id, actor: me?.name || me?.email || "" });
      toast.success(`Logged on ${r.filed?.sf || r.filed?.customer_name} — it's on the customer file timeline`);
      onLogged(r.filed);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg overflow-hidden" aria-describedby={undefined} data-testid="log-to-case-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><FolderInput className="w-4 h-4 text-indigo-600" />Log email on a customer file</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground border rounded px-2 py-1.5 bg-slate-50 truncate">
            {email.from_name || email.from_email} — {email.subject || "(no subject)"}
          </div>
          <CasePicker api={api} value={picked} onChange={setPicked} suggested={suggestedCase} caseLink={caseLink} />
          <p className="text-[10px] text-muted-foreground">
            Writes an email line (sender, subject, AI summary, Outlook link) onto the file's timeline and archives the email against the case. The email stays in the inbox.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !picked} data-testid="log-to-case-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FolderInput className="w-4 h-4 mr-1" />}
            {picked ? `Log on ${picked.sf || picked.customer_name}` : "Log on file"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
